import type { HistoryAccess, HistoryAccessClaimBody, HistoryData } from 'dex-app.cm';

import { getContractIdsFromRoute } from '@/components/trackers/HistoryDiagram/utils';
import type { Tracker, BaseContract, MarketMakerContract } from '@/types';

import { getHistoryByHistoryAccess, getHistoryByHistoryAccessFull } from './pactswap/generated/api';

const getAccess = async (trackerOrAccess: Tracker | HistoryAccess) => {
  if ('historyId' in trackerOrAccess) {
    const access = trackerOrAccess;
    return access;
  }

  let results: PromiseSettledResult<HistoryAccess | undefined>[] = [];

  const tracker = trackerOrAccess;

  const { route, contractRoute, unique } = tracker;

  if (contractRoute) {
    results = await Promise.allSettled(
      contractRoute
        .filter((contract): contract is BaseContract | MarketMakerContract => contract !== null)
        .map(async ({ contractId }) => {
          const data = await getHistoryByHistoryAccess({ contractId, historyId: unique });
          return data.historyAccess;
        })
    );
  } else {
    results = await Promise.allSettled(
      getContractIdsFromRoute({ route }).map(async (contractId) => {
        const data = await getHistoryByHistoryAccess({ contractId, historyId: unique });
        return data.historyAccess;
      })
    );
  }

  const access = results.find(
    (result): result is PromiseFulfilledResult<HistoryAccessClaimBody> =>
      result.status === 'fulfilled' && !!result.value
  )?.value;

  return access;
};

export async function getHistoryByTrackerOrAccess(trackerOrAccess: Tracker | HistoryAccess): Promise<HistoryData[]> {
  const access = await getAccess(trackerOrAccess);

  if (access) {
    const { route, contractRoute } = trackerOrAccess as Tracker;

    let results: PromiseSettledResult<HistoryData[]>[] = [];

    if (contractRoute) {
      results = await Promise.allSettled(
        contractRoute
          .filter((contract): contract is BaseContract | MarketMakerContract => contract !== null)
          .map(async ({ contractId }) => {
            const data = await getHistoryByHistoryAccessFull({
              contractId,
              historyId: access.historyId,
              timestamp: access.timestamp,
            });

            return data.history as unknown as HistoryData[];
          })
      );
    } else {
      results = await Promise.allSettled(
        getContractIdsFromRoute({ route, withAllDirections: true }).map(async (contractId) => {
          const data = await getHistoryByHistoryAccessFull({
            contractId,
            historyId: access.historyId,
            timestamp: access.timestamp,
          });

          return data.history as unknown as HistoryData[];
        })
      );
    }

    return results
      .filter((res): res is PromiseFulfilledResult<HistoryData[]> => res.status === 'fulfilled')
      .flatMap(({ value }) => value);
  }

  return [];
}
